import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaChevronRight, FaWhatsapp } from 'react-icons/fa';

import { Container, Cartao, Empresa, ListaFiliais, Filial, Links, LinkInterno } from './styles';

import FormInput from '../../components/FormInput';
import Button from '../../components/Button';
import Copyright from '../../components/Copyright';
import { buscarEmpresa, IEmpresaAcesso } from '../../services/empresa';
import { mascaraCgc } from '../../utils/mascaras';
import { LINK_WHATSAPP, LOGO } from '../../constants/contato';

// Último CNPJ informado neste navegador, para pré-preencher na próxima vez
const CHAVE_CNPJ = 'sigepeweb::acesso:cnpj';

function lerCnpjSalvo() {
  try {
    return localStorage.getItem(CHAVE_CNPJ) ?? '';
  } catch {
    return '';
  }
}

function salvarCnpj(cnpj: string) {
  try {
    localStorage.setItem(CHAVE_CNPJ, cnpj);
  } catch {
    // sem localStorage (aba anônima, bloqueado): só não lembra o CNPJ
  }
}

// Tela "Acessar plataforma" da landing page: o usuário informa o CNPJ, a API
// devolve as filiais (GET /empresa) e o front vai para /:cnpj/:filial/login.
export default function AcessarPlataforma() {
  const navigate = useNavigate();
  const [cnpj, setCnpj] = useState(() => mascaraCgc(lerCnpjSalvo()));
  const [carregando, setCarregando] = useState(false);
  const [empresa, setEmpresa] = useState<IEmpresaAcesso>();

  function irParaLogin(dados: IEmpresaAcesso, filial: string) {
    navigate(`/${dados.cnpj}/${encodeURIComponent(filial)}/login`);
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const digitos = cnpj.replace(/\D/g, '');

    if (digitos.length !== 14) {
      toast.warning('Informe os 14 dígitos do CNPJ da empresa.');
      return;
    }

    try {
      setCarregando(true);
      const dados = await buscarEmpresa(digitos);
      salvarCnpj(digitos);

      if (dados.filiais.length === 1) return irParaLogin(dados, dados.filiais[0]);
      setEmpresa(dados);
    } catch (error: any) {
      if (error.response?.data?.MENSAGEM) return toast.error(error.response.data.MENSAGEM);
      if (error.response?.status === 404) return toast.error('Empresa não encontrada, verifique o CNPJ.');
      if (!error.response) return toast.error('Não foi possível conectar ao servidor.');
      toast.error(error.message);
    } finally {
      setCarregando(false);
    }
  }

  return (
    <Container>
      <Cartao onSubmit={handleSubmit}>
        <img src={LOGO} alt='TI.Fire Sistemas' />
        <h1>Acessar o SigepeWeb</h1>

        {!empresa ? (
          <>
            <p className='subtitulo'>Informe o CNPJ da sua empresa para continuar</p>
            <FormInput
              placeHolder='CNPJ da empresa'
              required
              inputMode='numeric'
              autoComplete='off'
              maxLength={18}
              value={cnpj}
              onChange={e => setCnpj(mascaraCgc(e.target.value))}
              autoFocus
            />
            <Button texto='Continuar' type='submit' carregando={carregando} larguraTotal />
          </>
        ) : (
          <>
            <p className='subtitulo'>Escolha a filial que deseja acessar</p>
            <Empresa>
              <strong>{empresa.raz}</strong>
              <small>{mascaraCgc(empresa.cnpj)}</small>
            </Empresa>
            <ListaFiliais>
              {empresa.filiais.map(filial => (
                <li key={filial}>
                  <Filial type='button' onClick={() => irParaLogin(empresa, filial)}>
                    {filial}
                    <FaChevronRight aria-hidden />
                  </Filial>
                </li>
              ))}
            </ListaFiliais>
            <Button texto='Trocar CNPJ' variante='fantasma' onClick={() => setEmpresa(undefined)} larguraTotal />
          </>
        )}

        <Links>
          <LinkInterno to='/'>
            <FaArrowLeft aria-hidden />
            Voltar ao site
          </LinkInterno>
          <a className='whatsapp' href={LINK_WHATSAPP} target='_blank' rel='noreferrer'>
            <FaWhatsapp aria-hidden />
            Precisa de ajuda?
          </a>
        </Links>
      </Cartao>
      <Copyright />
    </Container>
  );
}
